import type { ISettingMaster } from "@bbj-types/setting";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Collapse, Stack, Typography } from "@mui/material";
import { getActionDate } from "@root/utils/actions";
import { useI18nContext } from "@root/utils/organisation";
import { type MouseEvent, useState } from "react";
import { PaperCandidateChallengeActionCard } from "./Action";
import { PaperCandidateChallengeActionsFilters } from "./filters";
import { usePaperCandidateChallengeActions } from "./logics";
import { PaperCandidateChallengeActionsRoot, paperCandidateChallengeActionsClasses } from "./styles";
import type { TPaperCandidateChallengeAction } from "./types";

export interface IPaperCandidateChallengeActionsProps<Action extends TPaperCandidateChallengeAction> {
  actions: Action[];
  actionTypes?: ISettingMaster["values"];
  actionChannels?: ISettingMaster["values"];
  onActionClick?: (event: MouseEvent, action: Action) => void;
}

export const PaperCandidateChallengeActions = <Action extends TPaperCandidateChallengeAction>({
  actions,
  actionTypes,
  actionChannels,
  onActionClick,
}: IPaperCandidateChallengeActionsProps<Action>) => {
  const { timeZone } = useI18nContext();
  const [open, setOpen] = useState(false);
  const { groupedActions, statusFilter, setStatusFilter } = usePaperCandidateChallengeActions(actions);

  return (
    <PaperCandidateChallengeActionsRoot className={paperCandidateChallengeActionsClasses.root}>
      <Stack className={paperCandidateChallengeActionsClasses.collapse} onClick={() => setOpen(!open)}>
        <Typography variant="body1SemiBold">Actions liées ({actions.length})</Typography>
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Stack>
      <Collapse in={open}>
        <Stack className={paperCandidateChallengeActionsClasses.actionsContainer}>
          <PaperCandidateChallengeActionsFilters statusFilter={statusFilter} setStatusFilter={setStatusFilter} />
          {groupedActions.map(
            (group) =>
              group.actions.length > 0 && (
                <Stack key={group.key} className={paperCandidateChallengeActionsClasses.actionsGroupContainer}>
                  <Typography
                    variant="body1SemiBold"
                    className={paperCandidateChallengeActionsClasses.actionsGroupLabel}
                  >
                    {getActionDate(group.actions[0])?.toLocaleString("fr-FR", {
                      month: "long",
                      year: "numeric",
                      timeZone,
                    })}
                  </Typography>
                  <Stack sx={{ flexDirection: "row", flexWrap: "wrap" }}>
                    {group.actions.map((action) => (
                      <PaperCandidateChallengeActionCard
                        key={action._id}
                        action={action}
                        actionTypes={actionTypes}
                        actionChannels={actionChannels}
                        onClick={onActionClick}
                      />
                    ))}
                  </Stack>
                </Stack>
              )
          )}
        </Stack>
      </Collapse>
    </PaperCandidateChallengeActionsRoot>
  );
};
